// Source of truth for the model + effort selectors.
//
// The model list and per-model effort levels mirror the official Anthropic
// docs (https://platform.claude.com/docs/en/build-with-claude/effort).
// Effort is supported on Fable 5.1, Fable 5, Opus 5.5, Opus 5, Opus 4.8,
// Opus 4.7, Opus 4.6, and Sonnet 4.6. The Claude 5 family (Fable 5.1/5,
// Opus 5.5/5) supports all five levels; xhigh is additionally available
// on Opus 4.8 and 4.7. Opus 5.5 is the only model that defaults to
// medium effort (docs: every other effort model defaults to high).
//
// Ordering doctrine: within a family, newer/longer IDs go FIRST, because
// modelLabel() falls back to a first-match prefix scan for dated IDs —
// with `claude-fable-5` listed before `claude-fable-5-1`, a Fable 5.1
// agent would render as "Fable 5".

import type { EffortLevel } from '../types';

export interface ModelOption {
  label: string;
  value: string;
  /** Effort levels this model supports. Empty = no effort control. */
  supportedEfforts: readonly EffortLevel[];
}

export const MODEL_OPTIONS: ReadonlyArray<ModelOption> = [
  { label: 'Fable 5.1',  value: 'claude-fable-5-1',           supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Fable 5',    value: 'claude-fable-5',             supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Opus 5.5',   value: 'claude-opus-5-5',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Opus 5',     value: 'claude-opus-5',              supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Opus 4.8',   value: 'claude-opus-4-8',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Opus 4.7',   value: 'claude-opus-4-7',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Opus 4.6',   value: 'claude-opus-4-6',            supportedEfforts: ['low', 'medium', 'high', 'max'] },
  { label: 'Opus 4.5',   value: 'claude-opus-4-5-20251101',   supportedEfforts: [] },
  // Oficina v2 (S0, T0.7, 27-09-2026): comprobado con `claude -p --model claude-sonnet-5 --effort xhigh|max`
  // en las dos cuentas Max (vps1 y vps2).
  { label: 'Sonnet 5',   value: 'claude-sonnet-5',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
  { label: 'Sonnet 4.6', value: 'claude-sonnet-4-6',          supportedEfforts: ['low', 'medium', 'high', 'max'] },
  { label: 'Sonnet 4.5', value: 'claude-sonnet-4-5-20250929', supportedEfforts: [] },
  { label: 'Haiku 4.5',  value: 'claude-haiku-4-5-20251001',  supportedEfforts: [] },
];

// Sprint default-model-fable-5-high (2026-06-09) — Fable 5 at effort=high
// is now the fleet-wide default for every new agent on every runner and
// every project. Triggered by Santi 2026-06-09 chat overriding an earlier
// conservative recommendation: "implementalo para que, por defecto, sea
// Fable 5 High en todos los runners y en todos los proyectos. Se pueda
// seleccionar y cambiar: modelos, esfuerzo, etc. Pero por defecto, Fable
// 5 High." Empirically verified on CLI 2.1.152: stream-json `init`,
// assistant `model`, and `result.modelUsage` all report `claude-fable-5`
// authoritatively (no silent fallback). Overage org-disabled on Max
// → bounded cost. All models + efforts remain user-selectable via the
// chip; this constant only sets the create-agent default.
export const DEFAULT_MODEL: string = 'claude-fable-5';
export const DEFAULT_EFFORT: EffortLevel = 'high';

// Sprint fix-opus-alias-drift (2026-06-03) — removed the ALIAS_MAP
// that translated CLI aliases ('opus'/'sonnet'/'haiku'/'best') into
// canonical IDs at render time. The map's `opus → claude-opus-4-8`
// disagreed with the Claude CLI's own alias resolution
// (`opus → claude-opus-4-7` on CLI 2.1.152), so any agent stored with
// `model: 'opus'` rendered as "Opus 4.8" in the chip but ran as 4.7.
// The fix is structural: the dashboard only stores canonical IDs
// end-to-end. Legacy rows with model='opus' surface the literal string
// in the chip until the one-off migration in
// `backend/src/db/migrations/037_canonicalize_model_aliases.sql`
// rewrites them — by design, so the lie is visible instead of papered over.

export interface EffortOption {
  label: string;
  value: EffortLevel;
}

export const EFFORT_OPTIONS: ReadonlyArray<EffortOption> = [
  { label: 'Low',        value: 'low' },
  { label: 'Medium',     value: 'medium' },
  { label: 'High',       value: 'high' },
  { label: 'Extra-high', value: 'xhigh' },
  { label: 'Max',        value: 'max' },
];

/** Look up the ModelOption for a canonical model ID. */
function findModel(model: string): ModelOption | undefined {
  return MODEL_OPTIONS.find((m) => m.value === model);
}

/**
 * Human label for a canonical model ID. Exact match wins; otherwise a
 * prefix match tolerates dated IDs (e.g. `claude-sonnet-4-5-20250929`
 * → "Sonnet 4.5"). Per fix-opus-alias-drift doctrine (above) an unknown
 * ID renders raw so drift stays visible instead of being papered over.
 */
export function modelLabel(modelId: string): string {
  const exact = findModel(modelId);
  if (exact) return exact.label;
  const prefix = MODEL_OPTIONS.find((m) => modelId.startsWith(m.value));
  return prefix ? prefix.label : modelId;
}

/** Effort options available for the given model. */
export function effortsForModel(model: string): EffortOption[] {
  const opt = findModel(model);
  const supported = opt?.supportedEfforts ?? [];
  if (supported.length === 0) return [];
  return EFFORT_OPTIONS.filter((e) => supported.includes(e.value));
}

/** True if the given model supports effort at all. */
export function modelSupportsEffort(model: string): boolean {
  return effortsForModel(model).length > 0;
}

/** Default effort for a given model per Anthropic docs. */
export function defaultEffortForModel(model: string): EffortLevel {
  const opt = findModel(model);
  if (!opt || opt.supportedEfforts.length === 0) return DEFAULT_EFFORT;
  if (opt.value === 'claude-fable-5') return 'high';
  // Opus 5.5 is the one model whose API default is medium, not high.
  if (opt.value === 'claude-opus-5-5') return 'medium';
  if (opt.value === 'claude-opus-4-7') return 'xhigh';
  if (opt.value === 'claude-opus-4-8') return 'high';
  return 'high';
}

/**
 * If the currently selected effort is no longer available on the new model,
 * fall back to that model's default; otherwise keep the existing choice.
 */
export function reconcileEffort(
  model: string,
  current: EffortLevel,
): EffortLevel {
  const available = effortsForModel(model);
  if (available.length === 0) return defaultEffortForModel(model);
  return available.some((e) => e.value === current)
    ? current
    : defaultEffortForModel(model);
}

// ---------- Prompt-length thresholds (D1) ----------

/**
 * Char-count thresholds for the prompt textarea. We use raw character counts
 * as a coarse proxy for token count (real tokenization varies). The numbers
 * align with the Sprint 5 spec.
 */
export const PROMPT_WARN_CHARS = 100_000;
export const PROMPT_MAX_CHARS = 150_000;
